import { Link } from 'react-router-dom';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { BottomSheet } from './bottom-sheet';
import { useLanguage } from './language-context';
import { useSignedInAccount } from './signed-in-account';
import { surfaceUrl } from './surface-links';
import { useTheme } from './theme-context';

// The tab bar's account sheet (design DC:3831-3849, copy + links DC:4787-4811). Two
// modes, like the design's `mobAuth`:
//   'bookings' — "Your bookings will live here" + Sign in (signed-out visitors only;
//               a signed-in visitor's Bookings tab links straight to the dashboard);
//   'more'     — the same header (signed out) plus the More list: saved places,
//               messages, reviews, the theme toggle, for-business and the resources.
//
// Account destinations live on the customer dashboard. Signed out they go through the
// central sign-in with that page as `next` (the design's `requireAuth`); signed in
// they link straight there. The design's "Notifications" row is not carried: DR-38
// replaces that page with the dashboard's top-bar tray.

export type AccountSheetMode = 'bookings' | 'more';

interface AccountLink {
  key: MessageKey;
  path: string;
}

const ACCOUNT_LINKS: AccountLink[] = [
  { key: 'sheet.saved_places', path: '/saved' },
  { key: 'account.messages', path: '/messages' },
  { key: 'account.my_reviews', path: '/reviews' },
];

const SITE_LINKS: Array<{ key: MessageKey; to: string }> = [
  { key: 'nav.for_business', to: '/for-business' },
  { key: 'nav.blog', to: '/blog' },
  { key: 'help.title', to: '/help' },
  { key: 'sheet.about', to: '/about' },
  { key: 'contact.title', to: '/contact' },
];

export function AccountSheet({ mode, onClose }: { mode: AccountSheetMode | null; onClose: () => void }) {
  const { t } = useLanguage();
  const { user } = useSignedInAccount();
  const { resolved, setPref } = useTheme();
  const open = mode !== null;
  const isBookings = mode === 'bookings';
  const title = t(isBookings ? 'sheet.bookings_title' : 'tabs.more');

  function accountHref(path: string): string {
    const target = surfaceUrl('app', path);
    if (user) return target ?? path;
    return target ? `/login?next=${encodeURIComponent(target)}` : '/login';
  }

  return (
    <BottomSheet open={open} onClose={onClose} title={title} grabber hideHead className="sheet--acct">
      {!user && (
        <div className="acctsheet__hero">
          <div className="acctsheet__ico" aria-hidden="true">
            {isBookings ? '▤' : '⋯'}
          </div>
          <h2 className="acctsheet__title">{title}</h2>
          <p className="acctsheet__sub">{t(isBookings ? 'sheet.bookings_sub' : 'sheet.more_sub')}</p>
          <Link
            className="acctsheet__cta"
            to={accountHref(isBookings ? '/bookings' : '/')}
            onClick={onClose}
          >
            {t('nav.sign_in')}
          </Link>
        </div>
      )}
      {mode === 'more' && (
        <ul className="acctsheet__list">
          {ACCOUNT_LINKS.map((l) => {
            const href = accountHref(l.path);
            return (
              <li key={l.key}>
                {href.startsWith('/') ? (
                  <Link className="acctsheet__row" to={href} onClick={onClose}>
                    {t(l.key)}
                    <span aria-hidden="true">›</span>
                  </Link>
                ) : (
                  <a className="acctsheet__row" href={href}>
                    {t(l.key)}
                    <span aria-hidden="true">›</span>
                  </a>
                )}
              </li>
            );
          })}
          <li>
            <button
              type="button"
              className="acctsheet__row"
              onClick={() => {
                setPref(resolved === 'dark' ? 'light' : 'dark');
                onClose();
              }}
            >
              {resolved === 'dark' ? `☀ ${t('sheet.light_mode')}` : `☾ ${t('sheet.dark_mode')}`}
              <span aria-hidden="true">›</span>
            </button>
          </li>
          {SITE_LINKS.map((l) => (
            <li key={l.key}>
              <Link className="acctsheet__row" to={l.to} onClick={onClose}>
                {t(l.key)}
                <span aria-hidden="true">›</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </BottomSheet>
  );
}
